import React, { useEffect, useState } from "react";
// @ts-ignore
import "./CancelConfirmationModal.scss";
import Modal from "../modal/Modal";
import { TSlidingModalAlign } from "../sliding-modal/SlidingModal";
import { Button, IButtonProps } from "../../../elements/button/Button";
import { COLORS } from "../../../models/colors";
import { VARIANTS } from "../../../models/variants";
import { bemElement } from "../../../utils/bem-class-names";
import { joinClassNames } from "../../../utils/join-class-names";
import { AxiosResponse } from "axios";
import { useAxios } from "providers/axios";
import { LoadingIcon } from "assets/Icons";

const baseClassName = "confirmation-modal";
const bem = bemElement(baseClassName);

export type RefundOption = "credits" | "refund";

interface ICancelConfirmationModalData {
  title?: string;
  text?: string;
  show: boolean;
  onHide: () => void;
  disableHeader?: boolean;
  disableBackground?: boolean;
  slideTo?: number;
  align?: TSlidingModalAlign;
  rightButton?: IButtonProps;
  onConfirm?: (refundOption?: RefundOption | null) => void;
  onReject?: () => void;
  className?: string;
  cyId?: string;
  cancelModalAppointmentId: string | null;
  setCancelModalAppointmentId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  isCompanyLimited: boolean;
}

interface Policy {
  late_cancellation_hours: number;
  late_cancellation_charge: number;
  no_show_charge: number;
}

interface Response {
  cancellation_type:
    | "lateFeeCancellation"
    | "noShowFeeCancellation"
    | "creditsGivenCancellation"
    | "noChargeNoCreditsCancellation";
  cancellation_result: CancellationResult;
  policy: Policy;
}

interface CancellationResult {
  cancellation_window: "late" | "no-show" | "none";
  cancellation_fee: number;
  total_fee?: number;
  total_credits?: number;
  amount_captured: number;
  total_fee_with_stripe_fees?: number;
  is_refundable: boolean;
  potential_stripe_refund_amount?: number;
}

const CancelConfirmationModal = ({
  title,
  text = "Are you sure?",
  show,
  onHide,
  disableHeader,
  disableBackground,
  slideTo,
  align = "bottom",
  rightButton,
  onConfirm,
  onReject,
  className = "",
  cyId = "",
  cancelModalAppointmentId,
  setCancelModalAppointmentId,
  isCompanyLimited
}: ICancelConfirmationModalData) => {
  const { api } = useAxios();
  const [cancellationData, setCancellationData] = useState<Response | null>(
    null
  );
  const [selectedOption, setSelectedOption] = useState<RefundOption | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = (option?: RefundOption | null) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    onConfirm?.(option);
  };

  const getCancellationData = async () => {
    const res: AxiosResponse<Response> = await api.post(
      `${process.env.REACT_APP_API_CORE_URL}/v1/cancellation-policy`,
      {
        appointment_id: cancelModalAppointmentId
      }
    );
    setCancellationData(res.data);
  };

  useEffect(() => {
    getCancellationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!show) {
      setSelectedOption(null);
    }
  }, [show]);

  const result = cancellationData?.cancellation_result;
  const isPaidByCustomer =
    result?.amount_captured !== undefined && result.amount_captured > 0;
  const isCancellationFee =
    result?.cancellation_fee !== undefined && result.cancellation_fee > 0;
  const isLateOrNoShowWindow =
    result?.cancellation_window === "late" ||
    result?.cancellation_window === "no-show";
  const isLateOrNoShowType =
    cancellationData?.cancellation_type === "lateFeeCancellation" ||
    cancellationData?.cancellation_type === "noShowFeeCancellation";

  const isRefundable = cancellationData?.cancellation_result?.is_refundable;

  const showRefundChoice =
    !isCompanyLimited && isPaidByCustomer && !isLateOrNoShowType;

  const hasLateCancellationFeeBreakdown = Boolean(
    isLateOrNoShowWindow &&
      cancellationData?.cancellation_type === "creditsGivenCancellation" &&
      result?.cancellation_fee &&
      result?.total_credits
  );

  const BottomControls = () => {
    if (showRefundChoice && selectedOption === null) {
      return (
        <div
          className={joinClassNames(
            bem("buttons-wrapper"),
            `${bem("buttons-wrapper")}--vertical`
          )}
        >
          {(result?.potential_stripe_refund_amount ?? 0) > 0 && (
            <Button
              color={COLORS.SUCCESS}
              variant={VARIANTS.FILLED}
              text="Refund (5-10 business days)"
              onClick={() => setSelectedOption("refund")}
              className="flexible"
            />
          )}
          <Button
            color={
              (result?.potential_stripe_refund_amount ?? 0) > 0
                ? COLORS.SECONDARY_CONTAINER
                : COLORS.SUCCESS
            }
            variant={VARIANTS.FILLED}
            text="Get store credits (instant)"
            onClick={() => setSelectedOption("credits")}
            className="flexible"
          />
          <Button
            color={COLORS.ERROR_CONTAINER}
            variant={VARIANTS.FILLED}
            text="No, Go Back"
            onClick={onReject}
            className="flexible"
          />
        </div>
      );
    }

    if (showRefundChoice && selectedOption !== null) {
      return (
        <div className={bem("buttons-wrapper")}>
          <Button
            color={COLORS.ERROR_CONTAINER}
            variant={VARIANTS.FILLED}
            text="No, Go Back"
            onClick={() => setSelectedOption(null)}
            className="flexible flex-1"
          />
          <Button
            color={COLORS.SUCCESS}
            variant={VARIANTS.FILLED}
            text="Yes, Cancel"
            cyId={cyId}
            onClick={() => handleConfirm(selectedOption)}
            disabled={isSubmitting}
            submitting={isSubmitting}
            className="flexible flex-2"
          />
        </div>
      );
    }

    return (
      <div className={bem("buttons-wrapper")}>
        <Button
          color={COLORS.ERROR_CONTAINER}
          variant={VARIANTS.FILLED}
          text="No, Go Back"
          onClick={onReject}
          className="flexible flex-1"
        />
        <Button
          color={COLORS.SUCCESS}
          variant={VARIANTS.FILLED}
          text="Yes, Cancel"
          cyId={cyId}
          onClick={() => handleConfirm()}
          disabled={isSubmitting}
          submitting={isSubmitting}
          className="flexible flex-2"
        />
      </div>
    );
  };

  if (!cancellationData) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        title={title}
        disableHeader={disableHeader}
        disableBackground={disableBackground}
        slideTo={slideTo}
        align={align}
        rightButton={rightButton}
        className={joinClassNames(baseClassName, className)}
        backgroundClassName={bem("background")}
      >
        <div className="w-full h-[120px] flex justify-center items-center">
          <div className="loadingSpinAnimation">
            <LoadingIcon />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={title}
      disableHeader={disableHeader}
      disableBackground={disableBackground}
      slideTo={slideTo}
      align={align}
      rightButton={rightButton}
      BottomControls={BottomControls}
      className={joinClassNames(baseClassName, className)}
      backgroundClassName={bem("background")}
    >
      <div className={bem("text")}>
        {showRefundChoice && selectedOption === "credits" && (
          <>
            {hasLateCancellationFeeBreakdown ? (
              <span>
                {" "}
                After applying a {result?.cancellation_window} cancellation fee,
                the remaining value will be applied as store credits to your
                account. Are you sure you wish to cancel this appointment?
              </span>
            ) : (
              <span>
                {" "}
                Store credits will be applied to your account and can be used at
                your next appointment. Are you sure you want to cancel?
              </span>
            )}
          </>
        )}
        {showRefundChoice && selectedOption === "refund" && (
          <>
            {hasLateCancellationFeeBreakdown ? (
              <span>
                {" "}
                After applying a {result?.cancellation_window} cancellation fee,
                the remaining value will be refunded to your original payment
                method. Please note that processing may take 5-10 business days.
                Are you sure you want to cancel this appointment?
              </span>
            ) : (
              <span>
                {" "}
                Please note that your refund may take 5-10 business days to
                process. Are you sure you want to cancel this appointment?
              </span>
            )}
          </>
        )}
        {showRefundChoice && selectedOption === null && (
          <>
            {hasLateCancellationFeeBreakdown ? (
              <span>
                {isPaidByCustomer ? "This appointment was prepaid." : ""}{" "}
                Because the cancellation falls within the{" "}
                {result?.cancellation_window} cancellation period, a{" "}
                {result?.cancellation_window} cancellation fee will apply. Upon
                cancellation,{" "}
                {cancellationData?.policy?.late_cancellation_charge * 100}% of
                the appointment value will be retained as a late cancellation
                fee. The remaining{" "}
                {100 - cancellationData?.policy?.late_cancellation_charge * 100}
                % will be refunded to you.{" "}
                {(result?.potential_stripe_refund_amount ?? 0) > 0
                  ? "How would you like to receive your refund?"
                  : ""}
              </span>
            ) : (
              <span>
                This appointment was prepaid. Your cancellation was early enough
                that you are eligible for a refund with no fees applied.{" "}
                {(result?.potential_stripe_refund_amount ?? 0) > 0
                  ? "How would you like to receive your refund?"
                  : ""}{" "}
                <br />
                <br />{" "}
                {(result?.potential_stripe_refund_amount ?? 0) > 0
                  ? `Store credits are added instantly to your account.
                  Refunds to your original payment method may take 5-10 business days.`
                  : ""}{" "}
              </span>
            )}
          </>
        )}

        {!showRefundChoice && (
          <>
            {!isCompanyLimited &&
              isPaidByCustomer &&
              (!isLateOrNoShowWindow || !result?.cancellation_fee) && (
                <span> This will give you store credits.</span>
              )}
            {!isCompanyLimited && isLateOrNoShowWindow && !isPaidByCustomer && (
              <span> You have not yet paid for this appointment</span>
            )}
            {/* {!isCompanyLimited && isLateOrNoShowWindow && isPaidByCustomer && (
              <span> You have already paid for this appointment.</span>
            )} */}

            {!isCompanyLimited &&
              isLateOrNoShowType &&
              isCancellationFee &&
              result?.total_fee_with_stripe_fees && (
                <span>
                  , but you are cancelling within the{" "}
                  {result?.cancellation_window} cancellation window. A{" "}
                  {result?.cancellation_window} cancellation fee will be charged
                  to the card on file. Are you sure you want to cancel this
                  appointment?
                </span>
              )}
            {!isLateOrNoShowType && !isCompanyLimited && (
              <span> Are you sure you want to cancel this appointment?</span>
            )}
            {!isCompanyLimited &&
              isLateOrNoShowWindow &&
              cancellationData?.cancellation_type ===
                "creditsGivenCancellation" &&
              result?.cancellation_fee &&
              result?.total_credits && (
                <span>
                  {" "}
                  After applying a {result?.cancellation_window} cancellation
                  fee, you will receive the remaining balance as store credits.
                </span>
              )}
            {isCompanyLimited && isRefundable && (
              <span>
                Are you sure you’d like to cancel your appointment? Once
                cancelled, the hold on your credit card will be released. Please
                note that processing may take 5-10 business days.
              </span>
            )}
            {isCompanyLimited && !isRefundable && (
              <span>
                This cancelation request is within 12 hours of the appointment's
                scheduled start time. By canceling, you will still be required
                to pay the full appointment fee. Are you sure you want to cancel
                this appointment?
              </span>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default CancelConfirmationModal;
